import useTranslation from 'next-translate/useTranslation'
import upperFirst from 'lodash/upperFirst'
import OrderTypeIcon from '@ancon/wildcat-ui/web/orderweb/OrderTypeIcon'
import { OrderFormat, ServiceTime } from '@ancon/wildcat-types'

import BodyText from '../../../app/components/BodyText'
import Button from '../../../app/components/Button'
import OrderFormatText from '../../../app/components/OrderFormatText'
import isOrderFormatDisplayable from '../../../app/utils/isOrderFormatDisplayable'
import getServiceTimeLabel from '../../../app/utils/getServiceTimeLabel'
import CompanyTag from '../../../company/components/CompanyTag'
import PreOrderAttendeesCountTag from '../../../preOrder/components/PreOrderAttendeesCountTag'
import isScheduledServiceTime from '../../utils/isScheduledServiceTime'
import CheckoutChip from '../CheckoutChip'

import styles from './CartHeaderSummary.module.scss'

type CartHeaderSummaryProps = {
  outletName: string
  orderFormat?: OrderFormat
  serviceTime?: ServiceTime
  isPreOrder: boolean
  companyName?: string | null
  isSetByHost: boolean
  onChangeSetup?: () => void
}

/** Where the order is had, for formats other than delivery */
function getWhereKey(orderFormat?: OrderFormat) {
  switch (orderFormat) {
    case OrderFormat.EatIn:
      return 'cart.eatInAt'
    case OrderFormat.TableOrder:
      return 'cart.tableOrderAt'
    case OrderFormat.DriveThru:
      return 'cart.driveThruAt'
    case OrderFormat.BoxPickup:
      return 'cart.boxPickupAt'
    default:
      return 'cart.pickUpAt'
  }
}

/** Cart header: who the order is for, what, when and where */
export default function CartHeaderSummary({
  outletName,
  orderFormat,
  serviceTime,
  isPreOrder,
  companyName,
  isSetByHost,
  onChangeSetup,
}: CartHeaderSummaryProps) {
  const { t } = useTranslation('common')

  const isScheduled = isScheduledServiceTime(serviceTime)

  const whenLine = upperFirst(
    isScheduled ? getServiceTimeLabel(serviceTime, t) : t('cart.whenNow'),
  )

  const whereLine = [
    orderFormat === OrderFormat.Delivery
      ? t('orderFormat.delivery')
      : t(getWhereKey(orderFormat), { outletName }),
    isSetByHost && t('cart.setByHost'),
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={styles.summary}>
      {companyName && (
        <CompanyTag className={styles.companyTag}>
          {t('cart.companyOrder', { companyName })}
        </CompanyTag>
      )}
      <div className={styles.tags}>
        {isOrderFormatDisplayable(orderFormat) && (
          <CheckoutChip>
            <OrderTypeIcon orderFormat={orderFormat!} />
            <OrderFormatText as="span" orderFormat={orderFormat!} />
          </CheckoutChip>
        )}
        {isScheduled && <CheckoutChip>{t('preOrder.title')}</CheckoutChip>}
        {isPreOrder && <PreOrderAttendeesCountTag />}
      </div>
      <div className={styles.when}>
        <div className={styles.whenText}>
          <BodyText as="p" className={styles.whenLine}>
            {whenLine}
          </BodyText>
          <BodyText as="p" color="body-1">
            {whereLine}
          </BodyText>
        </div>
        {onChangeSetup && (
          <Button variant="secondary" onClick={onChangeSetup}>
            {t('cart.change')}
          </Button>
        )}
      </div>
    </div>
  )
}
